import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, TextField } from "@mui/material";
import { DocumentCraftingMySettings } from "../../../Models/documentCrafting";
import {
  useDocumentCraftingMySettings,
  useUpdateDocumentCraftingMySettings,
} from "../../../Query/useDocumentCrafting";
import { getDocumentCraftingError } from "../../../Api/documentCrafting";
import { ErrorState, LoadingState, Panel } from "../Common/DocumentCraftingUi";

// The server's limits (settings_service.py: PHONE_MAX_LENGTH = 50,
// ROLE_MAX_LENGTH = 120; the role stays a bit shorter on purpose).
export const CONTACT_PHONE_MAX = 50;
export const CONTACT_ROLE_MAX = 100;
const PHONE_PATTERN = /^[+\d\s()/.-]*$/;

/** Field errors of the contact form ("" = valid). */
export const contactErrors = (phone: string, role: string) => ({
  phone:
    phone.trim().length > CONTACT_PHONE_MAX
      ? "tooLong"
      : PHONE_PATTERN.test(phone.trim())
        ? ""
        : "phoneInvalid",
  role: role.trim().length > CONTACT_ROLE_MAX ? "tooLong" : "",
});

type RejectedContactField = "phone" | "role" | "form";

/** Which part of the form a 400 `invalid_request` concerns. */
export const rejectedContactField = (error: unknown): RejectedContactField => {
  const field = getDocumentCraftingError(error)?.field;
  if (field === "contact_phone") return "phone";
  if (field === "contact_role") return "role";
  return "form";
};

const ContactForm: React.FC<{ settings: DocumentCraftingMySettings }> = ({
  settings,
}) => {
  const { t } = useTranslation("documentCrafting");
  const update = useUpdateDocumentCraftingMySettings();
  const [phone, setPhone] = useState(settings.contact_phone);
  const [role, setRole] = useState(settings.contact_role);
  const [rejected, setRejected] = useState<RejectedContactField | null>(null);

  const errors = contactErrors(phone, role);
  const changed =
    phone.trim() !== settings.contact_phone || role.trim() !== settings.contact_role;
  const valid = !errors.phone && !errors.role;
  const errorText = (field: "phone" | "role") => {
    const code = errors[field];
    if (code) {
      return t(`settings.contact.${code}`, {
        max: field === "phone" ? CONTACT_PHONE_MAX : CONTACT_ROLE_MAX,
      });
    }
    return rejected === field ? t("settings.rejectedValue") : undefined;
  };
  const edited = (field: "phone" | "role") => {
    if (rejected === field || rejected === "form") setRejected(null);
  };

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (!changed || !valid) return;
        setRejected(null);
        update.mutate(
          { data: { contact_phone: phone.trim(), contact_role: role.trim() } },
          {
            onError: (error) => {
              if (getDocumentCraftingError(error)?.code === "invalid_request") {
                setRejected(rejectedContactField(error));
              }
            },
          },
        );
      }}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {rejected === "form" && <Alert severity="error">{t("settings.rejectedForm")}</Alert>}
      <TextField
        size="small"
        label={t("settings.contact.phone")}
        placeholder={t("settings.contact.phonePlaceholder")}
        value={phone}
        onChange={(event) => {
          setPhone(event.target.value);
          edited("phone");
        }}
        error={Boolean(errors.phone) || rejected === "phone"}
        helperText={errorText("phone")}
        disabled={update.isPending}
        inputProps={{ inputMode: "tel", autoComplete: "tel" }}
      />
      <TextField
        size="small"
        label={t("settings.contact.role")}
        placeholder={t("settings.contact.rolePlaceholder")}
        value={role}
        onChange={(event) => {
          setRole(event.target.value);
          edited("role");
        }}
        error={Boolean(errors.role) || rejected === "role"}
        helperText={errorText("role") ?? t("settings.contact.roleHint")}
        disabled={update.isPending}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!changed || !valid || update.isPending}
        >
          {t("common.save")}
        </Button>
      </Box>
    </Box>
  );
};

/** "My contact details": phone and role, prefilled in new documents. */
const MyContactSettings: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const settings = useDocumentCraftingMySettings();
  const data = settings.data;

  return (
    <Panel
      title={t("settings.contact.title")}
      subtitle={t("settings.contact.subtitle")}
    >
      {data ? (
        // Remounts with the saved values.
        <ContactForm
          key={`${data.contact_phone}|${data.contact_role}`}
          settings={data}
        />
      ) : settings.isError ? (
        <ErrorState onRetry={() => settings.refetch()} />
      ) : (
        <LoadingState />
      )}
    </Panel>
  );
};

export default MyContactSettings;
